import './Anchor.scss';
import { useMemo } from 'react';
import Tooltip from '@/ui/tooltip';
import { isObject, isStringEmpty } from '@shared/utils';
import client from '@/store/client';
import globalStore from '@/store/global';
import { KNOWLEDGE_REFERER } from '../input-file/shared';
import { HelperCore } from '@/common/dependencies';
import Loader from '@/common/helper/loader';
import KnowledgeEnhanceStore from '@/components/chat-history/enhance-knowledge/store';
import { useLocalObservable, observer } from 'mobx-react-lite';
import { HelpOther } from '@/common/helper';
import { useConversationContext } from './context';

function Anchor(props) {
  const { className, href, children, tooltipable } = props;
  const index = Number(children);

  const {
    conversationId,
  } = useConversationContext();

  const knowledges = useMemo(() => (
    Array.isArray(props.references?.user_knowledge) ? props.references.user_knowledge : []
  ), [props.references]);
  const references = useMemo(() => (
    Array.isArray(props.references?.refer_search) ? props.references.refer_search : []
  ), [props.references]);
  const allList = useMemo(() => {
    return knowledges.concat(references);
  }, [references, knowledges]);

  const referenceInfo = useMemo(() => {
    if (Number.isNaN(index) || index <= 0 || index > allList.length) {
      return null;
    }
    return allList[index - 1];
  }, [index, allList]);

  const isKnowledge = index <= knowledges.length;

  if (!isStringEmpty(href)) {
    return (
      <a
        className={[globalStore.isDark ? '!text-[#4D8AFF]' : '!text-[#073D84FF]', '!underline', className]}
        target="_blank"
        href={href}
        onClick={
          event => {
            event.stopPropagation();
            if(client.isClient){
              client.run("OpenUrl", null, href);
              event.preventDefault();
            }
          }
        }
      >
        {children}
      </a>
    );
  }

  if (!isObject(referenceInfo)) {
    return null;
  }

  const fixedInfo = isKnowledge
  ? {
    ...referenceInfo,
    title : referenceInfo.file_name,
    config : KNOWLEDGE_REFERER[HelperCore.getFileNameExt(referenceInfo.file_name).toLowerCase()] || KNOWLEDGE_REFERER.others,
    format: HelperCore.getFileNameExt(referenceInfo.file_name).toUpperCase(),
    index,
  }
  : {
    ...referenceInfo,
    ...{
      index : index - knowledges.length,
    },
  };

  const store = useLocalObservable(() => new KnowledgeEnhanceStore(conversationId));
  const handleClick = async (event) => {
    if(!isKnowledge){
      return;
    }
    event.preventDefault();
    if(store.isOpening){
      return;
    }
    store.setOpening(true);

    if(!fixedInfo.ks_id){
      const knowledegeInfo = await Loader.getKnowledgeInfo(fixedInfo.folder_id);
      if(knowledegeInfo){
        fixedInfo.ks_id = knowledegeInfo.ks_id;
      }
    }
    let url = Loader.getFilePriviewUrl(fixedInfo);
    if (store.supportPreviewByEyun(fixedInfo.file_name)) {
      const result = await store.openWithWSCloud(fixedInfo)
      if (result) {
        if(result === true){
          store.setOpening(false);
          return;
        }
        if(result.errmsg){
          HelpOther.toast(result.errmsg, "error");
          store.setOpening(false);
          return;
        }
        else {
          url = result.downloadUrl;
        }
      }
    }
    else if (store.supportPreviewByWeishi(fixedInfo.file_name)) {
      const hasPermission = await store.checkPermission(fixedInfo.folder_id);
      if(!hasPermission){
        HelpOther.toast(Loader.previewPromissionMsg, "error");
        store.setOpening(false);
        return;
      }
      url = Loader.getFilePriviewUrl(fixedInfo);
    }
    else {
      HelpOther.toast("暂不支持该格式的文件预览", "error");
      store.setOpening(false);
      return;
    }

    store.setOpening(false);
    if (client.isLLQ) {
      client.openPage(url);
    } else if (client.isClient) {
      client.run("OpenUrl", null, url);
    } else {
      window.open(url);
    }
  }

  return tooltipable ? (
    <Tooltip
      overlayClassName="!max-w-360px"
      // open
      overlayInnerStyle={{
        borderRadius: '8px',
        border: ' 1px solid rgba(0, 0, 0, 0.1)',
        padding: '15px',
        lineHeight: 1,
        fontSize: 0,
      }}
      align={{ offset: [0, 10] }}
      trigger="hover"
      placement="bottom"
      color="#fff"
      arrow={false}
      title={
        <a
          className="inline-block w-full text-[14px] leading-[17px] !text-[#1465FF] truncate !hover:font-bold !hover:underline"
          href={fixedInfo.url}
          target="_blank"
          onClick={() => {}}
          onContextMenu={
            event => {
              if(isKnowledge){
                event.preventDefault();
              }
            }
          }
        >
          {fixedInfo.title}
        </a>
      }
    >
      <ReferenceAnchor className={className} href={fixedInfo.url} info={fixedInfo} onKnowledgeClick={handleClick}>
        {fixedInfo.index}
      </ReferenceAnchor>
    </Tooltip>
  ) : (
    <ReferenceAnchor className={className} href={fixedInfo.url} info={fixedInfo} onKnowledgeClick={handleClick}>
      {fixedInfo.index}
    </ReferenceAnchor>
  );
}1

const ReferenceAnchor = observer(props => {
  const { className, href, children, info, onKnowledgeClick = () => {}, ...reset } = props;
  // 知识库
  if(info?.config){
    return (
      <a
        {...reset}
        className={`!border-1px !border-solid rounded-full px-4px py-2px mx-8px relative -top-1px whitespace-nowrap inline-block text-12px font-bold-600 w-max select-none transform scale-90 origin-center`}
        style={{
          color : info.config.color,
          borderColor : info.config.color,
        }}
        target="_blank"
        href={href}
        onContextMenu={
          event => {
            event.preventDefault();
          }
        }
      >
        {children}
        <span
          className="opacity-30 mx-2px w-1px h-8px inline-block"
          style={{
            background: info.config.color,
          }}
          >
        </span>
        { info.config.text || info.format }
      </a>
    )
  }

  return (
    <a
      {...reset}
      className={[
        'reference-anchor',
        'inline-block',
        'ml-[4px]',
        'min-w-[18px]',
        'rounded-full px-[5px] py-[3px]',
        '!bg-[#0000001a]',
        'text-[12px] leading-none font-semibold',
        '!text-primary-opacity !text-opacity-50 !hover:text-white',
        'align-text-top',
        className,
      ]}
      target="_blank"
      href={href}
      onClick={
        event => {
          event.stopPropagation();
          if(client.isClient){
            client.run("OpenUrl", null, href);
            event.preventDefault();
          }
        }
      }
    >
      {children}
    </a>
  );
})

export default Anchor;
